/*
 * TTJ delivery gate (main product page only).
 *
 * Blocks Add to Cart and Buy it now until delivery to the entered pincode has been confirmed for THIS page.
 * Loaded by snippets/ttj-delivery-checker.liquid, so it only exists where the checker exists. Nothing is
 * blocked when the page has no delivery checker (e.g. the block was removed or disabled).
 *
 * Source of truth: <ttj-delivery-checker>.deliveryState / .deliveryPincode (assets/ttj-delivery-checker.js).
 * Add to Cart / Buy it now are allowed only when state === 'available' AND the input still holds the pincode
 * that was checked.
 *
 * Both buttons always stay visible AND clickable (no disabled state, no inert, no overlay on top of them):
 *   1. Add to Cart: a capture-phase `submit` listener on document stops the product form's submit before
 *      product-form.js sees it (preventDefault + stopImmediatePropagation). Covers the button, Enter in the
 *      quantity field, requestSubmit(), and the mobile sticky bar (it clicks the real submit button).
 *   2. Buy it now: a capture-phase `click` listener on document stops a click that lands anywhere inside the
 *      .shopify-payment-button wrapper before Shopify's own handler runs (composedPath() also sees through
 *      open shadow roots; keyboard activation produces a click, so Enter / Space are covered too).
 *      Both paths show the message directly above the buttons, and the note then follows the live state.
 *   Wallet buttons rendered as a direct <iframe> child of the wrapper cannot have their clicks intercepted from the
 *   page, so only those stay inert until delivery is confirmed. Buy it now itself is never inerted or restyled.
 *
 * Confirmed "delivery not available" (API said available === false for the entered pincode):
 *   - the amber note "Please enter another valid pincode to continue shopping." is shown at once (no click needed);
 *   - Add to Cart gets the grey look (data-ttj-gate-locked, assets/ttj-delivery-checker.css) but stays clickable;
 *     Buy it now keeps its normal look. A click on either shows the note and nothing is added or sent to checkout;
 *   - the lock ends when a newly checked pincode comes back available. (Typing a new pincode, loading and errors
 *     do not unlock; only an `available` result does.)
 */
(function () {
  'use strict';

  if (window.__ttjDeliveryGate) return;
  window.__ttjDeliveryGate = true;

  var MSG = {
    generic: 'Please enter a valid pincode with available delivery to continue.',
    empty: 'Please check delivery availability for your pincode before proceeding.',
    unchecked: 'Please check delivery availability for your pincode before proceeding.',
    loading: 'Checking delivery to your pincode. Please wait a moment.',
    error: 'We could not confirm delivery right now. Please click Check to try again.',
    unavailable: 'Please enter another valid pincode to continue shopping.',
  };

  var attempted = false; // set by a blocked click; afterwards the note follows the live state
  var locked = false; // true from a confirmed "unavailable" result until a newly checked pincode is available
  var lockNoteShown = false; // the note is currently showing because of the lock (so it can be removed on unlock)
  var observer = null;
  var observedForm = null;
  var rafId = 0;

  // The checker on the main product page (never the hidden copy inside a quick-add modal).
  function getChecker() {
    var list = document.querySelectorAll('ttj-delivery-checker');
    for (var i = 0; i < list.length; i++) {
      if (!list[i].closest('quick-add-modal') && list[i].input) return list[i];
    }
    return null;
  }

  function getForm(checker) {
    var scope = checker.closest('product-info') || checker.closest('section') || document;
    return scope.querySelector('form[data-type="add-to-cart-form"]');
  }

  // Returns the checker when `form` is the main product page's Add to Cart form, otherwise null.
  function guardedChecker(form) {
    if (!form || !form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return null;
    if (form.closest('quick-add-modal')) return null;
    var checker = getChecker();
    return checker && getForm(checker) === form ? checker : null;
  }

  function evaluate(checker) {
    var result = evaluateState(checker);
    // Lock on a confirmed "unavailable"; unlock only when delivery is confirmed available.
    if (result.unavailable) locked = true;
    else if (result.ok) locked = false;
    return result;
  }

  function evaluateState(checker) {
    var value = (checker.input.value || '').trim();
    var state = checker.deliveryState;

    if (state === 'loading') return { ok: false, message: MSG.loading };
    if (!value) return { ok: false, message: MSG.empty, focus: true };
    if (checker.deliveryPincode === value) {
      if (state === 'available') return { ok: true };
      if (state === 'unavailable') return { ok: false, unavailable: true, message: MSG.unavailable };
      if (state === 'error') return { ok: false, message: MSG.error };
    }
    if (!/^[0-9]{6}$/.test(value)) return { ok: false, message: MSG.generic, focus: true };
    return { ok: false, message: MSG.unchecked };
  }

  function getNote(form, create) {
    var note = form.querySelector('[data-ttj-gate-note]');
    if (note || !create) return note;
    var buttons = form.querySelector('.product-form__buttons');
    if (!buttons) return null;
    note = document.createElement('p');
    note.className = 'ttj-delivery-gate-note';
    note.setAttribute('data-ttj-gate-note', '');
    note.setAttribute('role', 'alert');
    note.hidden = true;
    buttons.parentNode.insertBefore(note, buttons); // directly above Add to Cart / Buy it now
    return note;
  }

  function showNote(form, message) {
    var note = getNote(form, true);
    if (!note) return null;
    if (note.hidden || note.textContent !== message) {
      note.textContent = message;
      note.hidden = false;
    }
    return note;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Called when the customer actually tried to add to cart / buy now and was stopped.
  function block(form, checker, result) {
    attempted = true;
    var note = showNote(form, locked ? MSG.unavailable : result.message); // while locked the note keeps one consistent message
    if (note) {
      var rect = note.getBoundingClientRect();
      var viewHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < 0 || rect.bottom > viewHeight) {
        // e.g. the mobile sticky bar was tapped while the buttons are off screen
        note.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }
    if (result.focus && checker.input && typeof checker.input.focus === 'function') {
      checker.input.focus({ preventScroll: true });
    }
  }

  /* ---------- Buy it now (dynamic checkout) ---------- */

  // "Buy it now" is never made inert, disabled, covered or restyled: Shopify may render it inside a host element, and
  // inerting that host made it look and act disabled. Only direct <iframe> children (cross-origin wallet buttons whose
  // clicks cannot be intercepted from the page) are made inert while blocked.
  function isBrandedWallet(child) {
    return child.tagName === 'IFRAME';
  }

  function syncBuyNow(form, blocked) {
    var wrapper = form.querySelector('.shopify-payment-button');
    if (!wrapper) return;
    var submit = form.querySelector('.product-form__submit');
    var soldOut = !!(submit && submit.hasAttribute('disabled'));

    if (blocked) {
      wrapper.setAttribute('data-ttj-gated', 'true');
      Array.prototype.forEach.call(wrapper.children, function (child) {
        if (!isBrandedWallet(child) || child.inert) return;
        child.inert = true;
        child.setAttribute('data-ttj-gate-inert', '');
      });
    } else {
      wrapper.removeAttribute('data-ttj-gated');
      Array.prototype.forEach.call(wrapper.children, function (child) {
        if (!child.hasAttribute('data-ttj-gate-inert')) return;
        child.removeAttribute('data-ttj-gate-inert');
        if (soldOut) {
          // Sold-out variant: hand the element back to assets/ttj-pdp-buy-actions.js, which keeps it inert
          child.setAttribute('data-ttj-inert', '');
        } else {
          child.inert = false;
        }
      });
    }
  }

  /* ---------- Add to Cart (confirmed unavailable) ---------- */

  // Grey look only: the button is never disabled here, so it stays clickable and can show the message.
  function syncAddToCart(form, isLocked) {
    var submit = form.querySelector('.product-form__submit');
    if (!submit) return;
    if (isLocked) {
      submit.setAttribute('data-ttj-gate-locked', ''); // grey look (assets/ttj-delivery-checker.css)
    } else {
      submit.removeAttribute('data-ttj-gate-locked');
    }
  }

  /* ---------- sync ---------- */

  function sync() {
    var checker = getChecker();
    var form = checker && getForm(checker);
    if (!form) return;

    watch(form);
    var result = evaluate(checker);
    syncAddToCart(form, locked); // first, so syncBuyNow sees the real (native) Add to Cart state
    syncBuyNow(form, !result.ok);

    if (locked) {
      // Confirmed unavailable: the note is visible straight away and stays until a deliverable pincode is checked.
      showNote(form, MSG.unavailable);
      lockNoteShown = true;
      return;
    }
    if (lockNoteShown) {
      var lockNote = getNote(form, false);
      if (lockNote) lockNote.hidden = true;
      lockNoteShown = false;
      attempted = false;
    }

    if (!attempted) return;
    if (result.ok) {
      var note = getNote(form, false);
      if (note) note.hidden = true;
      attempted = false;
    } else {
      showNote(form, result.message);
    }
  }

  function scheduleSync() {
    if (rafId) return;
    rafId = window.requestAnimationFrame(function () {
      rafId = 0;
      sync();
    });
  }

  // Shopify renders / re-renders the accelerated checkout buttons asynchronously.
  function watch(form) {
    if (observedForm === form || typeof MutationObserver !== 'function') return;
    if (observer) observer.disconnect();
    observedForm = form;
    observer = new MutationObserver(scheduleSync);
    observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['inert', 'disabled'] });
  }

  /* ---------- listeners ---------- */

  // 1. Add to Cart: capture phase on document runs before product-form.js's own submit listener.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      var checker = guardedChecker(form);
      if (!checker) return;
      var result = evaluate(checker);
      if (result.ok) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      block(form, checker, result);
    },
    true
  );

  // 2. Buy it now: capture phase on document runs before Shopify's own handler on the button. composedPath() lets
  //    this see clicks that originate inside open shadow roots (retargeted to the host outside).
  document.addEventListener(
    'click',
    function (event) {
      var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      var wrapper = null;
      for (var i = 0; i < path.length; i++) {
        if (path[i] && path[i].classList && path[i].classList.contains('shopify-payment-button')) {
          wrapper = path[i];
          break;
        }
      }
      if (!wrapper && event.target && event.target.closest) wrapper = event.target.closest('.shopify-payment-button');
      if (!wrapper) return;
      var form = wrapper.closest('form[data-type="add-to-cart-form"]');
      var checker = guardedChecker(form);
      if (!checker) return;
      var result = evaluate(checker);
      if (result.ok) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      block(form, checker, result);
    },
    true
  );

  document.addEventListener('ttj:delivery-state', scheduleSync);

  // A variant change re-renders the buttons: re-apply the current state afterwards.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, scheduleSync);
  }
  document.addEventListener('input', function (event) {
    if (event.target && event.target.classList && event.target.classList.contains('ttj-delivery__input')) scheduleSync();
  });

  sync();
})();
